/* eslint-disable @typescript-eslint/no-explicit-any */
import { HighlightsFormValues } from '@/hooks/usehighlightForm';
import { useToast } from '@/hooks/useToast';
import { useGeneratedStoreData } from '@/store/GeneratedDataStore/GeneratedStoreData';
import { useState } from 'react';
import { useHighlights } from '../../module/highlights.hook';
import {
  GenerateSectionOutput,
  HighlightOutput,
  QuestionOutput,
  QuestionType,
} from '@/api/gql/graphql';
import { QuizzFormValues } from '@/hooks/useQuizzForm';

export type Module = {
  section: GenerateSectionOutput;
  highlights: HighlightOutput[];
  quizz: QuestionOutput[];
};
export type Content = {
  content: string;
  title: string;
};

export const useGeneratedModule = () => {
  const { showToast } = useToast();
  const { generatedData, setGeneratedData } = useGeneratedStoreData();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [quizz, setQuizz] = useState<QuestionOutput[]>([]);
  const { handleClose } = useHighlights();
  const [generatedHighlightsId, setGeneratedHighlightsId] =
    useState<string>('');
  const [selectedHighlight, setSelectedHighlight] =
    useState<HighlightOutput | null>(null);
  const modules = generatedData?.sections || [];

  const currentHighlights = (moduleId: string) => {
    return (
      modules.find((module) => module.section._id === moduleId)?.highlights ||
      []
    );
  };

  const onSubmitGeneratedHighlight = (
    data: HighlightsFormValues,
    moduleId: string,
    highlightId: string,
  ) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    const moduleIndex = updatedData.sections.findIndex(
      (module: { section: { _id: string } }) =>
        module.section._id.toString() === moduleId,
    );

    const highlightIndex = updatedData.sections[
      moduleIndex
    ].highlights.findIndex(
      (highlight: HighlightOutput) => highlight._id.toString() === highlightId,
    );

    if (highlightIndex === -1 || moduleIndex === -1) {
      showToast('Erreur lors de la mise à jour du mot', 'error');
      return;
    }

    // Update the highlight with the new data
    updatedData.sections[moduleIndex].highlights[highlightIndex] = {
      ...updatedData.sections[moduleIndex].highlights[highlightIndex],
      term: data.term,
      slug: data.term,
      definition: {
        html: data.definition,
        text: data.definition,
      },
    };

    setGeneratedData(updatedData as any);
    showToast('Mot mis à jour avec succès', 'success');
    handleClose();
  };

  const onSubmitGeneratedContent = (data: Content, moduleId: string) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    const moduleIndex = updatedData.sections.findIndex(
      (module: { section: { _id: string } }) =>
        module.section._id.toString() === moduleId,
    );

    if (moduleIndex === -1) {
      showToast('Module non trouvé', 'error');
      return;
    }

    try {
      if (!updatedData.sections[moduleIndex].section) {
        updatedData.sections[moduleIndex].section = {
          _id: '',
          lessonsId: '',
          position: 0,
          content: [],
          title: '',
        };
      }

      updatedData.sections[moduleIndex].section.content[0].html = data.content;
      updatedData.sections[moduleIndex].section.title = data.title;

      setGeneratedData(updatedData as any);
      showToast('Contenu mis à jour avec succès', 'success');
    } catch (error) {
      console.error('Error updating content:', error);
      showToast('Erreur lors de la mise à jour du contenu', 'error');
    }
  };

  const onSubmitGeneratedQuizz = (
    data: QuizzFormValues,
    moduleId: string,
    quizzId: string,
    selectedType: QuestionType,
    response1Check: boolean,
    response2Check: boolean,
    response3Check: boolean,
    response4Check: boolean,
  ) => {
    // Create a proper deep clone of the data to avoid reference issues
    const updatedData = structuredClone(generatedData);

    if (!updatedData) {
      showToast('Données non disponibles', 'error');
      return;
    }

    const moduleIndex = updatedData.sections.findIndex(
      (module: { section: { _id: string } }) =>
        module.section._id.toString() === moduleId,
    );

    if (moduleIndex === -1) {
      showToast('Module non trouvé', 'error');
      return;
    }

    try {
      if (!updatedData.sections[moduleIndex].quizz) {
        updatedData.sections[moduleIndex].quizz = [];
      }

      const quizzIndex = updatedData.sections[moduleIndex].quizz.findIndex(
        (quiz: QuestionOutput) => quiz._id === quizzId,
      );

      if (quizzIndex === -1) {
        showToast('Quiz non trouvé', 'error');
        return;
      }

      // Get current quiz and responses for safer access
      const currentQuiz = updatedData.sections[moduleIndex].quizz[quizzIndex];
      const currentResponses = currentQuiz.responses || [];

      updatedData.sections[moduleIndex].quizz[quizzIndex] = {
        ...currentQuiz,
        name: data.name || '',
        type: selectedType,
        responses: [
          {
            ...(currentResponses[0] || {}),
            response: data.reponse1 || '',
            isRight: response1Check,
          },
          {
            ...(currentResponses[1] || {}),
            response: data.reponse2 || '',
            isRight: response2Check,
          },
          {
            ...(currentResponses[2] || {}),
            response: data.reponse3 || '',
            isRight: response3Check,
          },
          {
            ...(currentResponses[3] || {}),
            response: data.reponse4 || '',
            isRight: response4Check,
          },
        ],
      };

      setGeneratedData(updatedData);
      showToast('Quiz mis à jour avec succès', 'success');
      handleClose();
    } catch (error) {
      console.error('Error updating quiz:', error);
      showToast('Erreur lors de la mise à jour du quiz', 'error');
    }
  };

  const onSubmitGeneratedFinalExam = (
    data: QuizzFormValues,
    activeTabExercise: number,
    quizzId: string,
    selectedType: QuestionType,
    response1Check: boolean,
    response2Check: boolean,
    response3Check: boolean,
    response4Check: boolean,
  ) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    try {
      setGeneratedData(updatedData as any);
      showToast('Question mise à jour avec succès', 'success');
      handleClose();
    } catch (error) {
      console.error('Error updating final exam question:', error);
      showToast('Erreur lors de la mise à jour de la question', 'error');
    }
  };

  const deleteGeneratedQuizz = (quizzId: string) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    updatedData.sections.forEach((section: { quizz: QuestionOutput[] }) => {
      section.quizz = section.quizz.filter(
        (quiz: QuestionOutput) => quiz._id !== quizzId,
      );
    });
    setGeneratedData(updatedData as any);
    showToast('Quiz supprimé avec succès', 'success');
  };

  const deleteGeneratedFinalExam = (quizzId: string) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    setGeneratedData(updatedData as any);
    showToast('Question supprimée avec succès', 'success');
  };

  const updateGeneratedFinalExamExercice = (
    data: QuizzFormValues,
    resetForm: () => void,
    closeModal: () => void,
    activeTabExercise: number,
  ) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    setGeneratedData(updatedData as any);
    showToast('Exercice mis à jour avec succès', 'success');
    resetForm();
    closeModal();
  };

  const deleteGeneratedFinalExamExercice = (exoIndex: number) => {
    const updatedData = structuredClone(generatedData);
    if (!updatedData) return;

    setGeneratedData(updatedData as any);
    showToast('Exercice supprimé avec succès', 'success');
  };

  return {
    showToast,
    modules,
    currentHighlights,
    generatedHighlightsId,
    setGeneratedHighlightsId,
    selectedHighlight,
    setSelectedHighlight,
    onSubmitGeneratedHighlight,
    onSubmitGeneratedContent,
    onSubmitGeneratedQuizz,
    deleteGeneratedQuizz,
    onSubmitGeneratedFinalExam,
    deleteGeneratedFinalExam,
    updateGeneratedFinalExamExercice,
    deleteGeneratedFinalExamExercice,
    content,
    setContent,
    title,
    setTitle,
    quizz,
    setQuizz,
  };
};
